import en from './en.json';
import de from './de.json';
import { defineStoredString } from '../utils/storage';

export const defaultLocale = 'en';

/**
 * Supported UI locales and their message catalogs. Add a locale by dropping a
 * sibling JSON file and registering it here, layered over `en`.
 *
 * Every non-default catalog is spread on top of English so a key missing from
 * the translation falls back to the English string instead of the raw message
 * id — translations may be partial.
 */
export const messagesByLocale: Record<string, Record<string, string>> = {
    en,
    de: { ...en, ...de }
};

export type AppLocale = keyof typeof messagesByLocale;

/**
 * The languages the switcher offers, each named in ITSELF (an endonym) — that is
 * what a reader looking for their own language scans for, so these are not
 * translated and carry no i18n key.
 */
export const LOCALES: { code: string; label: string }[] = [
    { code: 'de', label: 'Deutsch' },
    { code: 'en', label: 'English' }
];

/**
 * `allow` is the catalog itself: a locale we no longer ship reads as "never
 * picked one", which is better than rendering raw message ids. Exported for
 * `useStoredValue`, so a switch in one tab reaches the others.
 */
export const localeValue = defineStoredString({
    key: 'locale',
    allow: Object.keys(messagesByLocale)
});

/** Remember an explicit choice, so it survives reloads and other devices' defaults. */
export function storeLocale(locale: string): void {
    // Not fatal when it does not land: the choice then only lasts this session.
    localeValue.write(locale);
}

/**
 * Pick the best supported locale from the browser's language preferences,
 * matching on the base tag (e.g. 'de-AT' -> 'de'). Falls back to the default
 * locale when none are supported.
 *
 * Only consulted until the user picks a language themselves — an explicit
 * choice always wins, and nothing is written here until they make one.
 */
export function resolveLocale(): string {
    const preferences = navigator.languages ?? [navigator.language];
    for (const tag of preferences) {
        const baseTag = tag.toLowerCase().split('-')[0];
        if (baseTag in messagesByLocale) {
            return baseTag;
        }
    }
    return defaultLocale;
}
